/**
 * DTO zod — le CONTRAT entre le BFF et le front.
 * Frontière stricte : tout ce qui sort du BFF est validé par ces schémas.
 * Règles issues du plan directeur :
 *  - RGPD : AUCUN champ patient ne traverse cette frontière (test dédié).
 *  - Champs incomplets côté Vevidental (docteur 80 %, échéance 98 %) → nullable,
 *    jamais optionnels silencieux : le front doit gérer l'absence explicitement.
 *  - `createdAt` du travail = proxy de date de réception (pas de dateReception).
 */
import { z } from "zod";
import { EchangesSchema } from "./echanges";

/* ---------------------------------------------------------------- statuts */

/** Statuts de travail — valeurs API Twenty (relevé 2026-07-28). */
export const STATUTS_TRAVAIL = [
  "RECU",
  "CFAO",
  "METALLURGIE", // libellé UI : Conjointe
  "CERAMIQUE",
  "ADJOINTE",
  "CONTROLE_QUALITE", // libellé UI : Contrôle
  "EXPEDIE",
  "FACTURE",
] as const;
export type StatutTravail = (typeof STATUTS_TRAVAIL)[number];
export const StatutTravailSchema = z.enum(STATUTS_TRAVAIL);

/** Statuts terminaux : un travail dans cet état n'est plus « en cours ». */
export const STATUTS_TERMINAUX: readonly StatutTravail[] = ["EXPEDIE", "FACTURE"];

export const STATUTS_FACTURE = ["PAYEE", "PARTIELLE", "EN_RETARD", "IMPAYEE"] as const;
export type StatutFacture = (typeof STATUTS_FACTURE)[number];
export const StatutFactureSchema = z.enum(STATUTS_FACTURE);

/** Segment d'activité — CALCULÉ, jamais éditable (roadmap §fiche 360). */
export const SEGMENTS = ["actif", "tiede", "silencieux", "dormant", "inactif"] as const;
export type Segment = (typeof SEGMENTS)[number];
export const SegmentSchema = z.enum(SEGMENTS);

/** Statut relationnel — décidé par un HUMAIN, 5 valeurs en v1 (13 bible en v2). */
export const STATUTS_RELATION = [
  "actif",
  "a_surveiller",
  "en_reactivation",
  "en_litige",
  "perdu",
] as const;
export type StatutRelation = (typeof STATUTS_RELATION)[number];
export const StatutRelationSchema = z.enum(STATUTS_RELATION);

/* ------------------------------------------------------------- primitives */

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}/, "date ISO attendue");
const Uuid = z.string().uuid();

/** Horodatage de synchronisation — OBLIGATOIRE sur toute réponse (piège n°1). */
export const SyncMetaSchema = z.object({
  syncedAt: z.string().datetime({ offset: true }).nullable(),
  /**
   * D'où viennent VRAIMENT les données servies.
   *
   * Élargi le 2026-08-12 : la liste Cabinets annonçait « twenty » pour
   * 1 253 cliniques venues de la copie API, et la page Clients s'apprêtait à
   * faire pareil. Un écran qui nomme mal sa source rend toute vérification
   * impossible — c'est exactement ce qui a laissé la copie muette 33 jours.
   * La file « À relancer » distingue déjà les deux (`relances.ts`).
   */
  source: z.enum(["twenty", "vevidental_api"]),
  cacheAgeSeconds: z.number().int().min(0),
  /**
   * Âge RÉEL de la copie Vevidental, en secondes — la dernière ingestion
   * réussie, lue en base.
   *
   * `cacheAgeSeconds` juste au-dessus mesure autre chose : l'âge d'une entrée
   * du cache mémoire du serveur. C'est ce qu'affichaient les écrans, et il ment
   * dans le sens rassurant — un serveur qui redémarre a un cache vide, donc un
   * âge de 0, et annonce « données fraîches » avec une copie de trois jours.
   * C'est ce qui a permis à la copie de rester muette 33 jours.
   *
   * `null` = aucune passe n'a jamais réussi. Ce n'est pas 0 : « on ne sait
   * pas » et « c'est frais » sont deux réponses différentes.
   */
  copieAgeSeconds: z.number().int().min(0).nullable(),
});
export type SyncMeta = z.infer<typeof SyncMetaSchema>;

/* --------------------------------------------------------------- cabinets */

/**
 * Une ligne de la liste Cabinets = une CLINIQUE Vevidental (1 253), pas une
 * fiche Twenty (127).
 *
 * Ce basculement date du 2026-08-12 : l'activité était calculée sur les seuls
 * travaux présents dans Twenty, qui n'en porte qu'un extrait. La page annonçait
 * « 34 cabinets n'ont jamais commandé » quand la copie API en connaît 126 avec
 * un historique, et affichait 0 dormant alors qu'il y en a 482. Voir
 * `apps/bff/src/vevidental/cabinets-source.ts` pour les mesures.
 *
 * Conséquence sur la clé : `idVevidental` est TOUJOURS là et sert de clé de
 * liste ; `id` (fiche Twenty) devient nullable — une clinique sans fiche est
 * visible et appelable, mais ne s'ouvre pas, il n'y a rien à ouvrir.
 */
export const CabinetListItemSchema = z.object({
  /** fiche Twenty — `null` tant qu'aucune fiche n'existe pour cette clinique */
  id: Uuid.nullable(),
  /** identifiant Vevidental — clé stable de la ligne, jamais nulle */
  idVevidental: z.number().int().positive(),
  nom: z.string().min(1),
  ville: z.string().nullable(),
  telephone: z.string().nullable(),
  /** la case « portable » de Vevidental (`mobile_phone`), à côté du fixe — GO Dove 03/09 */
  mobile: z.string().nullable().default(null),
  email: z.string().nullable(),
  segment: SegmentSchema,
  vip: z.boolean(),
  dernierTravailJours: z.number().int().min(0).nullable(), // null = jamais de travail
  /** volume historique — distingue un dormant à rappeler d'une fiche vide */
  nbTravaux: z.number().int().min(0),
  travauxEnCours: z.number().int().min(0),
  echeancesDepassees: z.number().int().min(0),
  /** null si le rôle du demandeur n'a pas accès à la finance (RBAC). */
  solde: z.number().nullable(),
});
export type CabinetListItem = z.infer<typeof CabinetListItemSchema>;

export const CabinetsListResponseSchema = z.object({
  items: z.array(CabinetListItemSchema),
  total: z.number().int().min(0),
  /** nombre de lignes disposant d'une fiche Twenty (le reste n'est pas ouvrable) */
  avecFiche: z.number().int().min(0),
  sync: SyncMetaSchema,
});
export type CabinetsListResponse = z.infer<typeof CabinetsListResponseSchema>;

/* ------------------------------------------------------- liste d'appels */

/**
 * Une ligne de liste d'appels — ce qu'il faut AVOIR SOUS LES YEUX pour appeler
 * un cabinet qui a décroché : qui, comment le joindre, depuis quand il ne
 * confie plus rien, et ce qu'il commandait.
 *
 * Le dernier point est celui qui change la nature de l'appel : sans lui on dit
 * « on ne vous voit plus », avec lui on dit « vous nous confiiez surtout des
 * couronnes zircone, la dernière il y a 14 mois ».
 */
export const LigneAppelSchema = z.object({
  idVevidental: z.number().int().positive(),
  id: Uuid.nullable(),
  nom: z.string().min(1),
  ville: z.string().nullable(),
  telephone: z.string().nullable(),
  mobile: z.string().nullable().default(null),
  email: z.string().nullable(),
  dernierTravailJours: z.number().int().min(0).nullable(),
  nbTravaux: z.number().int().min(0),
  produits: z.array(z.object({ nom: z.string(), nb: z.number().int().min(1) })),
});
export type LigneAppel = z.infer<typeof LigneAppelSchema>;

export const ListeAppelsResponseSchema = z.object({
  lignes: z.array(LigneAppelSchema),
  /** demandés mais introuvables (lien périmé, clinique disparue de la copie) */
  ignores: z.number().int().min(0),
});
export type ListeAppelsResponse = z.infer<typeof ListeAppelsResponseSchema>;

/* -------------------------------------------------------------- fiche 360 */

export const TravailResumeSchema = z.object({
  id: Uuid,
  code: z.string(), // "2072776" — parsé du name Twenty "code · docteur · date"
  statut: StatutTravailSchema,
  secteur: z.string().nullable(),
  parcours: z.string().nullable(), // "CFAO → Céramique"
  typeTravail: z.string().nullable(),
  teinte: z.string().nullable(),
  dents: z.string().nullable(),
  echeance: IsoDate.nullable(), // 2 % sans échéance : « sans échéance », jamais « en retard »
  echeanceDepassee: z.boolean(),
  recuLe: IsoDate, // createdAt Twenty (proxy réception)
  praticienNom: z.string().nullable(), // 20 % sans docteur
  prescriptionAttachmentId: Uuid.nullable(),
});
export type TravailResume = z.infer<typeof TravailResumeSchema>;

export const KpiFicheSchema = z.object({
  dernierTravailJours: z.number().int().min(0).nullable(),
  /** Médiane des écarts entre réceptions (min 5 travaux) — null = « non établie ». */
  cadenceJours: z.number().min(0).nullable(),
  travaux12Mois: z.number().int().min(0),
  travauxEnCours: z.number().int().min(0),
  echeancesDepassees: z.number().int().min(0),
  retours90j: z.number().int().min(0),
  travauxExpedies90j: z.number().int().min(0), // dénominateur du ratio retours
  /** Bloc finance entier null si RBAC refuse (jamais de zéro trompeur). */
  finance: z
    .object({
      solde: z.number(),
      nonEchu: z.number(),
      facturesEnRetard: z.number().int().min(0),
      totalEnRetard: z.number(),
    })
    .nullable(),
});
export type KpiFiche = z.infer<typeof KpiFicheSchema>;

/** Événement de chronologie — union discriminée (bible §10, §24.2). */
export const TimelineEventSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("travail_recu"),
    date: IsoDate,
    travailId: Uuid,
    code: z.string(),
    typeTravail: z.string().nullable(),
  }),
  z.object({
    kind: z.literal("travail_expedie"),
    date: IsoDate,
    travailId: Uuid,
    code: z.string(),
  }),
  z.object({
    kind: z.literal("retour"),
    date: IsoDate,
    retourId: Uuid,
    motif: z.string().nullable(),
    travailCode: z.string().nullable(),
  }),
  z.object({
    kind: z.literal("facture"),
    date: IsoDate,
    factureId: Uuid,
    numero: z.string(),
    statut: StatutFactureSchema,
    montant: z.number(),
  }),
  z.object({
    kind: z.literal("reglement"),
    date: IsoDate,
    factureId: Uuid,
    numero: z.string(),
    montant: z.number(),
  }),
  z.object({
    kind: z.literal("course"),
    date: IsoDate,
    courseId: Uuid,
    type: z.string(),
    statut: z.string(),
  }),
  z.object({
    kind: z.literal("note"),
    date: IsoDate,
    noteId: Uuid,
    titre: z.string(),
    extrait: z.string(),
    auteur: z.string().nullable(),
  }),
  z.object({
    kind: z.literal("tache"),
    date: IsoDate,
    tacheId: Uuid,
    titre: z.string(),
    extrait: z.string(),
    /** TODO | IN_PROGRESS | DONE (valeurs Twenty) */
    statut: z.string(),
    echeance: IsoDate.nullable(),
    auteur: z.string().nullable(),
  }),
]);
export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

export const Fiche360Schema = z.object({
  cabinet: z.object({
    id: Uuid,
    nom: z.string(),
    adresse: z.string().nullable(),
    ville: z.string().nullable(),
    telephone: z.string().nullable(),
    /** portable de la clinique Vevidental rapprochée ; `null` sans rapprochement */
    mobile: z.string().nullable().default(null),
    email: z.string().nullable(),
    segment: SegmentSchema,
    vip: z.boolean(),
    statutRelation: StatutRelationSchema,
    /**
     * Code cabinet Vevidental (C2, sprint 3) — vit dans le menu « ⋯ » de la
     * fiche. `null` = fiche non rapprochée (`correspondance_cabinet` muette) ;
     * le champ est OBLIGATOIRE pour qu'un serveur qui l'oublie rougisse ici,
     * pas en production.
     */
    idVevidental: z.number().int().positive().nullable(),
  }),
  praticiens: z.array(
    z.object({
      id: Uuid,
      nom: z.string(),
      email: z.string().nullable(),
      telephone: z.string().nullable(),
    }),
  ),
  kpi: KpiFicheSchema,
  travauxEnCours: z.array(TravailResumeSchema),
  timeline: z.array(TimelineEventSchema),
  timelineCursor: z.string().nullable(),
  /** e-mails et WhatsApp du cabinet dans un fil unique (§9.3) */
  echanges: EchangesSchema,
  sync: SyncMetaSchema,
});
export type Fiche360 = z.infer<typeof Fiche360Schema>;

/* ---------------------------------------------------------------- session */

export const MeSchema = z.object({
  email: z.string().email(),
  name: z.string(),
  /** Multi-rôles (bible §4.1, E9) — au moins un rôle. */
  roles: z
    .array(z.enum(["super_admin", "admin", "secretariat", "commercial", "production", "technicien", "comptabilite", "logistique", "lecture"]))
    .min(1),
  /** Permissions nominatives auditables (ex. arbitrage Estelle, §4.2). */
  permissions: z.array(z.enum(["arbitrage", "publication"])),
  /**
   * Vrai quand la source Twenty est COUPÉE EXPRÈS sur cet environnement
   * (staging : hôte sentinelle `.invalid`). Optionnel : un serveur plus
   * ancien ne l'envoie pas, et son absence vaut « vivant ».
   */
  twentyCoupe: z.boolean().optional(),
});
export type Me = z.infer<typeof MeSchema>;
